import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Trophy,
  Star,
  Clock,
  Heart,
  XCircle,
  ArrowRight,
  Award,
  Target,
  CheckCircle,
  Brain,
  Code,
  Database,
  Lightbulb,
  Puzzle,
  Zap
} from 'lucide-react';
import Button from '../ui/Button';

interface SkillChallengeExperienceProps {
  isOpen: boolean;
  onClose: () => void;
  challenge: any;
}

// Different types of challenges
const challengeTasks = {
  'coding': [
    {
      id: 1,
      type: 'implementation',
      description: 'Implement a function to find the longest common subsequence of two strings',
      content: `function findLCS(str1, str2) {
  // Your implementation here
}`,
      testCases: [
        { input: ['ABCDGH', 'AEDFHR'], expected: 'ADH' },
        { input: ['AGGTAB', 'GXTXAYB'], expected: 'GTAB' }
      ],
      hints: [
        'Consider using dynamic programming',
        'Create a matrix to store intermediate results',
        'Think about the base cases first'
      ]
    },
    {
      id: 2,
      type: 'debugging',
      description: 'Fix the bugs in this binary search implementation',
      content: `function binarySearch(arr, target) {
  let left = 0;
  let right = arr.length;
  
  while (left < right) {
    let mid = (left + right) / 2;
    if (arr[mid] === target) return mid;
    if (arr[mid] < target) left = mid;
    else right = mid;
  }
  return -1;
}`,
      bugs: [
        'Integer division needed for mid',
        'Infinite loop possible',
        'Right boundary incorrect'
      ]
    }
  ],
  'system_design': [
    {
      id: 1,
      type: 'architecture',
      description: 'Design a scalable social media feed system',
      requirements: [
        'Support millions of users',
        'Real-time updates',
        'Content moderation',
        'Media storage'
      ],
      components: [
        'Load Balancer',
        'Application Servers',
        'Cache Layer',
        'Database'
      ]
    }
  ],
  'problem_solving': [
    {
      id: 1,
      type: 'algorithm',
      description: 'Optimize an e-commerce checkout process',
      scenario: 'Users are abandoning carts during checkout. Analyze and improve the process.',
      constraints: [
        'Payment processing time < 2s',
        'Support multiple payment methods',
        'Handle concurrent transactions'
      ]
    }
  ],
  'data_analysis': [
    {
      id: 1,
      type: 'analysis',
      description: 'Analyze customer churn patterns',
      dataset: {
        description: 'Customer data including usage patterns, billing history, and support interactions',
        size: '10,000 records',
        features: ['usage_frequency', 'billing_amount', 'support_tickets', 'last_login']
      },
      tasks: [
        'Identify key churn indicators',
        'Create predictive model',
        'Recommend retention strategies'
      ]
    }
  ]
};

const SkillChallengeExperience: React.FC<SkillChallengeExperienceProps> = ({
  isOpen,
  onClose,
  challenge
}) => {
  const [lives, setLives] = useState(3);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(challenge.duration * 60);
  const [currentTask, setCurrentTask] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackType, setFeedbackType] = useState<'success' | 'error'>('success');
  const [gameOver, setGameOver] = useState(false);
  const [earnedBadges, setEarnedBadges] = useState<string[]>([]);
  const [currentHint, setCurrentHint] = useState(0);
  const [showHint, setShowHint] = useState(false);
  const [solution, setSolution] = useState('');
  const [testResults, setTestResults] = useState<any[]>([]);

  useEffect(() => {
    if (isOpen && !gameOver) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            setGameOver(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isOpen, gameOver]);

  const handleSubmitSolution = () => {
    // Simulate solution validation
    const isCorrect = Math.random() > 0.3; // 70% success rate for demo
    
    if (isCorrect) {
      setScore(prev => prev + 100);
      setFeedbackType('success');
      if (score + 100 >= 250 && !earnedBadges.includes('problem-solver')) {
        setEarnedBadges(prev => [...prev, 'problem-solver']);
      }
    } else {
      setLives(prev => prev - 1);
      setFeedbackType('error');
      if (lives <= 1) {
        setGameOver(true);
      }
    }

    setShowFeedback(true);
    setTimeout(() => {
      setShowFeedback(false);
      if (currentTask < challengeTasks[challenge.type].length - 1) {
        setCurrentTask(prev => prev + 1);
        setSolution('');
        setTestResults([]);
      } else {
        setGameOver(true);
      }
    }, 1500);
  };

  const handleRunTests = () => {
    // Simulate test execution
    const results = challengeTasks[challenge.type][currentTask].testCases?.map(test => ({
      input: test.input,
      expected: test.expected,
      actual: 'Test output',
      passed: Math.random() > 0.3
    }));
    setTestResults(results || []);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getTaskIcon = (type: string) => {
    switch (type) {
      case 'implementation':
        return <Code className="h-6 w-6" />;
      case 'debugging':
        return <Puzzle className="h-6 w-6" />;
      case 'architecture':
        return <Database className="h-6 w-6" />;
      case 'algorithm':
        return <Brain className="h-6 w-6" />;
      case 'analysis':
        return <Lightbulb className="h-6 w-6" />;
      default:
        return <Zap className="h-6 w-6" />;
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={onClose}
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative bg-white rounded-2xl shadow-xl max-w-4xl w-full p-6 md:p-8"
            >
              {/* Game Header */}
              <div className="flex justify-between items-center mb-8">
                <div className="flex items-center space-x-4">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Heart
                      key={i}
                      className={`h-6 w-6 ${i < lives ? 'text-red-500' : 'text-gray-300'}`}
                      fill={i < lives ? 'currentColor' : 'none'}
                    />
                  ))}
                </div>
                <div className="text-2xl font-bold text-primary-600">
                  {score} pts
                </div>
                <div className="flex items-center text-gray-600">
                  <Clock className="h-5 w-5 mr-2" />
                  {formatTime(timeLeft)}
                </div>
              </div>

              {!gameOver ? (
                <>
                  {/* Current Task */}
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        {getTaskIcon(challengeTasks[challenge.type][currentTask].type)}
                        <h3 className="text-xl font-semibold text-gray-900 ml-2">
                          {challengeTasks[challenge.type][currentTask].description}
                        </h3>
                      </div>
                      <div className="text-sm text-gray-500">
                        Task {currentTask + 1} of {challengeTasks[challenge.type].length}
                      </div>
                    </div>

                    {/* Task Content */}
                    {challengeTasks[challenge.type][currentTask].content && (
                      <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg mb-6 overflow-x-auto">
                        <code>{challengeTasks[challenge.type][currentTask].content}</code>
                      </pre>
                    )}

                    {/* Solution Input */}
                    <div className="mb-6">
                      <textarea
                        value={solution}
                        onChange={(e) => setSolution(e.target.value)}
                        className="w-full h-48 font-mono bg-gray-50 p-4 rounded-lg border border-gray-200 focus:ring-2 focus:ring-primary-500"
                        placeholder="Write your solution here..."
                      />
                    </div>

                    {/* Test Results */}
                    {testResults.length > 0 && (
                      <div className="mb-6">
                        <h4 className="font-semibold text-gray-900 mb-2">Test Results</h4>
                        <div className="space-y-2">
                          {testResults.map((result, index) => (
                            <div
                              key={index}
                              className={`p-3 rounded-lg ${
                                result.passed ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                              }`}
                            >
                              <div className="flex items-center">
                                {result.passed ? (
                                  <CheckCircle className="h-5 w-5 mr-2" />
                                ) : (
                                  <XCircle className="h-5 w-5 mr-2" />
                                )}
                                <span>Test Case {index + 1}</span>
                              </div>
                              <div className="mt-1 text-sm">
                                <div>Input: {JSON.stringify(result.input)}</div>
                                <div>Expected: {result.expected}</div>
                                <div>Actual: {result.actual}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-between">
                      <Button
                        variant="outline"
                        onClick={() => setShowHint(true)}
                      >
                        Get Hint
                      </Button>
                      <div className="space-x-4">
                        <Button
                          variant="outline"
                          onClick={handleRunTests}
                        >
                          Run Tests
                        </Button>
                        <Button
                          onClick={handleSubmitSolution}
                        >
                          Submit Solution
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Hint Modal */}
                  <AnimatePresence>
                    {showHint && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-2xl"
                      >
                        <div className="bg-white p-6 rounded-xl max-w-md">
                          <h4 className="text-lg font-semibold text-gray-900 mb-4">
                            Hint {currentHint + 1}
                          </h4>
                          <p className="text-gray-600 mb-6">
                            {challengeTasks[challenge.type][currentTask].hints?.[currentHint]}
                          </p>
                          <div className="flex justify-end gap-4">
                            <Button
                              variant="outline"
                              onClick={() => setShowHint(false)}
                            >
                              Close
                            </Button>
                            {currentHint < (challengeTasks[challenge.type][currentTask].hints?.length || 0) - 1 && (
                              <Button
                                onClick={() => {
                                  setCurrentHint(prev => prev + 1);
                                  setScore(prev => Math.max(0, prev - 10)); // Penalty for using hints
                                }}
                              >
                                Next Hint (-10 pts)
                              </Button>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Feedback Overlay */}
                  <AnimatePresence>
                    {showFeedback && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-2xl"
                      >
                        <div className={`p-8 rounded-full ${
                          feedbackType === 'success' ? 'bg-green-500' : 'bg-red-500'
                        }`}>
                          {feedbackType === 'success' ? (
                            <CheckCircle className="h-16 w-16 text-white" />
                          ) : (
                            <XCircle className="h-16 w-16 text-white" />
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </>
              ) : (
                // Game Over Screen
                <div className="text-center">
                  <div className="mb-8">
                    {score >= 250 ? (
                      <div className="inline-block p-6 bg-primary-100 rounded-full mb-4">
                        <Trophy className="h-16 w-16 text-primary-600" />
                      </div>
                    ) : (
                      <div className="inline-block p-6 bg-gray-100 rounded-full mb-4">
                        <Target className="h-16 w-16 text-gray-600" />
                      </div>
                    )}
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      Challenge Complete!
                    </h2>
                    <p className="text-gray-600">
                      {score >= 250 
                        ? 'Congratulations! You\'ve mastered this skill challenge!' 
                        : 'Keep practicing to improve your skills!'}
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-6 mb-8">
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="text-center">
                        <div className="text-3xl font-bold text-primary-600 mb-1">
                          {score}
                        </div>
                        <div className="text-sm text-gray-500">Points Earned</div>
                      </div>
                      <div className="text-center">
                        <div className="text-3xl font-bold text-primary-600 mb-1">
                          {earnedBadges.length}
                        </div>
                        <div className="text-sm text-gray-500">Badges Earned</div>
                      </div>
                    </div>

                    {earnedBadges.length > 0 && (
                      <div className="flex justify-center gap-4 mb-6">
                        {earnedBadges.map((badge) => (
                          <div key={badge} className="text-center">
                            <div className="p-3 bg-primary-100 rounded-full mb-2">
                              <Award className="h-6 w-6 text-primary-600" />
                            </div>
                            <div className="text-sm font-medium text-gray-700">
                              {badge}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex justify-center gap-4">
                    <Button
                      variant="outline"
                      onClick={onClose}
                    >
                      Close
                    </Button>
                    <Button
                      onClick={() => {
                        setLives(3);
                        setScore(0);
                        setTimeLeft(challenge.duration * 60);
                        setCurrentTask(0);
                        setGameOver(false);
                        setEarnedBadges([]);
                        setSolution('');
                        setTestResults([]);
                        setCurrentHint(0);
                      }}
                      icon={<ArrowRight className="h-4 w-4" />}
                      iconPosition="right"
                    >
                      Try Again
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default SkillChallengeExperience;